import {
  IPricesAndOptionsReqSchema,
  IProduct,
  IRateReqSchema,
  IRateReqSchemaUI,
  OfferingTypesEnum,
  PercentOfTotalConfigTypeEnum,
  ProductTypeEnum,
  RateStatusEnum,
  RateTypeEnum,
  RateUsageBillingFrequencyEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { RATE_BILLING_FREQUENCY_MAP } from '../constants/offerings';
import {
  getOptionsFromProductObject,
  getPricesFromProductObject,
} from '../routes/ProductCatalog/Offerings/components/Rate/RatePriceUtils';

/**
 * Builds the percentOfTotalConfig for initial rate data
 */
export const buildPercentOfTotalConfig = (
  config: Maybe<{
    productIds?: Maybe<string[]>;
    type: PercentOfTotalConfigTypeEnum;
  }>,
  offeringType: OfferingTypesEnum,
): IRateReqSchema['percentOfTotalConfig'] => {
  if (offeringType !== OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL) {
    return null;
  }

  if (config?.type === PercentOfTotalConfigTypeEnum.SPECIFIED_PRODUCTS) {
    return {
      ...config,
      productIds: config.productIds ?? [],
      type: PercentOfTotalConfigTypeEnum.SPECIFIED_PRODUCTS,
      minimumTotal: null,
      maximumTotal: null,
    };
  }

  return {
    type: PercentOfTotalConfigTypeEnum.ALL_ELIGIBLE,
    minimumTotal: null,
    maximumTotal: null,
  };
};

/**
 * Builds the percentOfTotalConfig for form submission
 */
export const buildPercentOfTotalConfigForSubmit = (
  formData: IRateReqSchemaUI,
  offeringType: OfferingTypesEnum,
): IRateReqSchema['percentOfTotalConfig'] => {
  if (offeringType !== OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL) {
    return null;
  }

  const config = formData.percentOfTotalConfig;

  if (config?.type === PercentOfTotalConfigTypeEnum.SPECIFIED_PRODUCTS) {
    return {
      ...config,
      productIds: config.productIds || [],
      minimumTotal: config.minimumTotal,
      maximumTotal: config.maximumTotal,
    };
  }

  return {
    type: PercentOfTotalConfigTypeEnum.ALL_ELIGIBLE,
    minimumTotal: config?.minimumTotal ?? null,
    maximumTotal: config?.maximumTotal ?? null,
  };
};

/**
 * Converts billing frequency to months
 */
export const getBillingFrequencyInMonths = (
  formData: IRateReqSchemaUI,
): number | null => {
  if (formData?.billingFrequencyInMonths) {
    return formData.billingFrequencyInMonths;
  }

  if (formData?.billingFrequency) {
    return (
      RATE_BILLING_FREQUENCY_MAP[formData.billingFrequency]().month ?? null
    );
  }

  return null;
};

/**
 * Gets product options for the rate based on offering type and product types
 */
export const getProductOptions = (
  formProduct: Record<string, IPricesAndOptionsReqSchema>,
  products: IProduct[],
  offeringType: OfferingTypesEnum,
) => {
  const isAnyUsageProductExist = products.some(
    ({ productType }) => productType === ProductTypeEnum.USAGE,
  );

  if (offeringType === OfferingTypesEnum.SUBSCRIPTION) {
    return getOptionsFromProductObject({
      products: formProduct,
      offeringType,
      isAnyUsageProductExist,
    });
  }

  if (isAnyUsageProductExist) {
    return getOptionsFromProductObject({
      products: formProduct,
      isAnyUsageProductExist,
    });
  }

  return [];
};

/**
 * Transforms form data into API request payload
 */
export const transformFormDataToRateRequest = (
  formData: IRateReqSchemaUI,
  products: IProduct[],
  offeringType: OfferingTypesEnum,
): IRateReqSchema => {
  const { products: datProducts, ...rest } = formData;

  return {
    ...rest,
    prices: getPricesFromProductObject(formData.products),
    options: {
      priceDisplay: rest.options?.priceDisplay,
      productOptions: getProductOptions(
        formData.products,
        products,
        offeringType,
      ),
      tokenAllocationFrequencyInMonth:
        rest?.options?.tokenAllocationFrequencyInMonth ?? null,
    },
    usageBillingFrequency: formData.usageBillingFrequency ?? null,
    percentOfTotalConfig: buildPercentOfTotalConfigForSubmit(
      formData,
      offeringType,
    ),
    billingFrequencyInMonths: formData.billingFrequencyInMonths
      ? formData.billingFrequencyInMonths
      : formData.billingFrequency
        ? RATE_BILLING_FREQUENCY_MAP[formData.billingFrequency]().month
        : null,
    rateType: rest.rateType ?? RateTypeEnum.CATALOG,
  };
};

/**
 * Transforms form values into status update request payload
 */
export const transformFormDataToStatusUpdate = (
  values: IRateReqSchemaUI,
  status: RateStatusEnum,
  products: IProduct[],
  offeringType: OfferingTypesEnum,
): IRateReqSchema => {
  const {
    products: offeringProducts,
    usageBillingFrequency: formUsageBillingFrequency,
    ...rest
  } = values;

  return {
    ...rest,
    status,
    rateType: rest.rateType ?? RateTypeEnum.CATALOG,
    prices: getPricesFromProductObject(offeringProducts),
    options: {
      priceDisplay: rest.options?.priceDisplay,
      productOptions: getProductOptions(
        offeringProducts,
        products,
        offeringType,
      ),
      tokenAllocationFrequencyInMonth:
        rest.options?.tokenAllocationFrequencyInMonth ?? null,
    },
    usageBillingFrequency:
      formUsageBillingFrequency === RateUsageBillingFrequencyEnum.MONTHLY
        ? RateUsageBillingFrequencyEnum.MONTHLY
        : null,
    percentOfTotalConfig: buildPercentOfTotalConfig(
      values.percentOfTotalConfig,
      offeringType,
    ),
    billingFrequencyInMonths: getBillingFrequencyInMonths(values),
  };
};
